import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  EyeIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { apiService, Subscription } from '../services/api';
import SubscriptionCreateModal from './SubscriptionCreateModal';
import SubscriptionViewModal from './SubscriptionViewModal';
import ConfirmModal from './ConfirmModal';

interface SubscriptionManagementProps {
  onBack?: () => void;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ onBack }) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [allSubscriptions, setAllSubscriptions] = useState<Subscription[]>([]); // For stats calculation
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    subscriptionId: number | null;
    message: string;
  }>({
    visible: false,
    subscriptionId: null,
    message: '',
  });

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
      };

      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await apiService.getSubscriptions({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter as any : undefined,
      });
      setSubscriptions(response.subscriptions || []);
      setTotal(response.pagination?.total || 0);

      // Fetch all subscriptions for stats calculation (without pagination)
      const statsParams: any = {
        page: 1,
        limit: 10000, // Large limit to get all
      };
      if (statusFilter !== 'all') statsParams.status = statusFilter;

      const statsResponse = await apiService.getSubscriptions(statsParams);
      setAllSubscriptions(statsResponse.subscriptions || []);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleCreate = () => {
    setIsCreateModalVisible(true);
  };

  const handleView = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setIsViewModalVisible(true);
  };

  const handleDelete = (id: number) => {
    const subscription = subscriptions.find(s => s.id === id);
    const subscriptionInfo = subscription 
      ? `subscription for "${subscription.user?.name || subscription.user?.email || 'user'}" with product "${subscription.productName || 'product'}"`
      : 'this subscription';
    
    setConfirmModal({
      visible: true,
      subscriptionId: id,
      message: `Are you sure you want to delete ${subscriptionInfo}?`,
    });
  };

  const confirmDelete = async () => {
    if (!confirmModal.subscriptionId) return;
    
    try {
      await apiService.deleteSubscription(confirmModal.subscriptionId);
      fetchSubscriptions();
      setConfirmModal({ visible: false, subscriptionId: null, message: '' });
    } catch (error) {
      console.error('Failed to delete subscription:', error);
      alert('Failed to delete subscription. Please try again.');
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await apiService.restoreSubscription(id);
      fetchSubscriptions();
    } catch (error) {
      console.error('Failed to restore subscription:', error);
    }
  };

  const handleCreateSuccess = () => {
    setIsCreateModalVisible(false);
    fetchSubscriptions();
  };

  const getStats = () => {
    // Use allSubscriptions for accurate stats calculation
    const subscriptionsForStats = allSubscriptions.length > 0 ? allSubscriptions : subscriptions;

    const activeCount = subscriptionsForStats.filter(s => s.status === 'ACTIVE').length;

    // Calculate total revenue - ensure currentPrice is converted to number
    const totalRevenue = subscriptionsForStats.reduce((sum, s) => {
      // Handle Decimal type from Prisma (might be string or number)
      let price: number;
      if (typeof s.currentPrice === 'number') {
        price = s.currentPrice;
      } else if (typeof s.currentPrice === 'string') {
        price = parseFloat(s.currentPrice);
      } else {
        price = 0;
      }

      // Ensure valid number
      if (isNaN(price) || !isFinite(price)) {
        price = 0;
      }

      return sum + price;
    }, 0);

    // Calculate average price
    const avgPrice = subscriptionsForStats.length > 0 ? totalRevenue / subscriptionsForStats.length : 0;

    return { activeCount, totalRevenue, avgPrice };
  };

  const stats = getStats();

  const formatPrice = (price: number) => {
    // Ensure price is a valid number
    const numPrice = typeof price === 'number' ? price : parseFloat(String(price || 0));
    if (isNaN(numPrice) || !isFinite(numPrice)) {
      return 'Rp 0';
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numPrice);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800';
      case 'ONGOING':
        return 'bg-yellow-100 text-yellow-800';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Subscription Management</h1>
        </div>
        <div className="text-sm text-gray-500">
          Total Subscriptions: {total}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Active Subscriptions</div>
          <div className="text-2xl font-bold text-gray-900">{stats.activeCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Revenue</div>
          <div className="text-2xl font-bold text-gray-900">{formatPrice(stats.totalRevenue)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Average Price</div>
          <div className="text-2xl font-bold text-gray-900">{formatPrice(stats.avgPrice)}</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by user email, name, or product name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="all">All</option>
              <option value="ACTIVE">Active</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="EXPIRED">Expired</option>
              <option value="ONGOING">Ongoing</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Page Size:</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <button
            onClick={handleCreate}
            className="bg-imola-primary hover:bg-imola-primary/80 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add Subscription
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trial
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ended At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                    No subscriptions found
                  </td>
                </tr>
              ) : (
                subscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                        {subscription.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 text-left">
                        {subscription.user?.name || subscription.user?.email || `User #${subscription.userId}`}
                      </div>
                      <div className="text-xs text-gray-500 text-left">{subscription.user?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 text-left">{subscription.productName}</div>
                      <div className="text-xs text-gray-500 text-left">{subscription.productSlug}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 text-left">
                        {formatPrice(subscription.currentPrice)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}>
                        {subscription.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subscription.isTrial
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                        }`}>
                        {subscription.isTrial ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <div className="text-sm text-gray-900">{formatDate(subscription.startedAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <div className="text-sm text-gray-900">{formatDate(subscription.endedAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(subscription)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(subscription.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                        {subscription.status === 'INACTIVE' || subscription.status === 'EXPIRED' ? (
                          <button
                            onClick={() => handleRestore(subscription.id)}
                            className="text-yellow-600 hover:text-yellow-900 p-1"
                            title="Restore"
                          >
                            <ArrowPathIcon className="w-4 h-4" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                disabled={currentPage >= Math.ceil(total / pageSize)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, total)}
                  </span>{' '}
                  of <span className="font-medium">{total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(total / pageSize)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <SubscriptionCreateModal
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />

      <SubscriptionViewModal
        visible={isViewModalVisible}
        subscription={selectedSubscription}
        onCancel={() => {
          setIsViewModalVisible(false);
          setSelectedSubscription(null);
        }}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        visible={confirmModal.visible}
        title="Confirm Delete"
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmModal({ visible: false, subscriptionId: null, message: '' })}
      />
    </div>
  );
};

export default SubscriptionManagement;
